import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import QRCodeStyling from 'qr-code-styling';
import { FRONTEND_BASE_URL } from 'utils/apiUtils';
import { useQRCodeContext } from 'contexts/QrCodeContext';
import { useAuth } from 'contexts/AuthContext';

const TeamTemplateQRCodePreview = ({ qrCode = {} }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const qrRef = useRef(null); // Store QRCodeStyling instance
    const containerRef = useRef(null); // DOM element to append QR code to
    const { authState } = useAuth();
    const { qrCodeState } = useQRCodeContext();

    // Get authenticated user's cardId for QR code URL
    const userCardId = authState.user?.activeCardId || 
                      (authState.user?.contactCardIds?.length > 0 ? authState.user.contactCardIds[0] : null);

    // Get QR code settings from context (for authenticated user's card) or from template props
    const contextQrCodeColor = userCardId ? (qrCodeState[userCardId]?.contextQrCodeColor || '#000000') : '#000000';
    const contextQrCodeLogoUrl = userCardId ? (qrCodeState[userCardId]?.contextQrCodeLogoUrl || '') : '';
    
    // Use context values if available, otherwise fall back to template props
    const qrCodeColor = contextQrCodeColor !== '#000000' ? contextQrCodeColor : (qrCode?.color || '#000000');
    const qrCodeLogoUrl = contextQrCodeLogoUrl || (qrCode?.logoUrl || '');
    const qrCodeSize = qrCode?.size || 200;
    const errorCorrectionLevel = qrCode?.errorCorrectionLevel || 'H';

    const BASE_URL = `${FRONTEND_BASE_URL}/card`;
    
    // Generate card URL using authenticated user's cardId
    const cardUrl = useMemo(() => {
        if (!userCardId) return '';
        return `${BASE_URL}/${userCardId}?source=qr_code`;
    }, [BASE_URL, userCardId]);

    // Create QR code instance
    useEffect(() => {
        const initializeQR = async () => {
            try {
                if (containerRef.current && cardUrl) {
                    setLoading(true);
                    setError('');

                    // Clear previous QR code
                    containerRef.current.innerHTML = '';

                    // Create QR code with same styling as team member page
                    qrRef.current = new QRCodeStyling({
                        width: qrCodeSize,
                        height: qrCodeSize,
                        type: "png", // Match team member page
                        data: cardUrl,
                        image: qrCodeLogoUrl || undefined, // Only include if logo exists
                        dotsOptions: {
                            color: qrCodeColor,
                            type: "square" // Match team member page
                        },
                        cornersSquareOptions: {
                            type: "square" // Match team member page
                        },
                        cornersDotOptions: {
                            type: "square" // Match team member page
                        },
                        backgroundOptions: {
                            color: "#ffffff"
                        },
                        imageOptions: {
                            crossOrigin: "anonymous",
                            margin: 7, // Match team member page
                            imageSize: 0.5, // Match team member page
                            hideBackgroundDots: qrCodeLogoUrl ? true : false, // Match team member page
                        },
                        qrOptions: {
                            errorCorrectionLevel: errorCorrectionLevel
                        }
                    });

                    // Append QR code to container
                    await qrRef.current.append(containerRef.current);
                    setLoading(false);
                } else if (!cardUrl) {
                    setError('Card ID not available');
                    setLoading(false);
                }
            } catch (err) {
                console.error('Error initializing QR code:', err);
                setError('Failed to generate QR code preview');
                setLoading(false);
            }
        };

        initializeQR();
    }, [cardUrl, qrCodeColor, qrCodeLogoUrl, qrCodeSize, errorCorrectionLevel]);

    // Get card full name for display (similar to team member page)
    const cardFullName = authState.user?.contact_info?.fullName || 
                        authState.user?.fullName || 
                        authState.user?.displayName || 
                        '';

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 2,
                maxWidth: '500px',
                margin: '20px',
            }}
        >
            {loading && <CircularProgress />}

            {error && (
                <Typography variant="body1" color="error">
                    {error}
                </Typography>
            )}

            <Box textAlign="center">
                <Typography
                    variant="h6"
                    sx={{
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        color: '#828282',
                    }}
                >
                    {cardFullName ? `${cardFullName}'s QRCode` : 'QR Code'}
                </Typography>
                <Box
                    ref={containerRef}
                    sx={{
                        marginTop: '40px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '300px',
                        '& svg': {
                            transition: 'all 0.3s ease-in-out',
                        }
                    }}
                />
            </Box>
        </Box>
    );
};

export default TeamTemplateQRCodePreview;

